/**
 * Shared layout constants for ODP card rendering.
 * Used by: card-builder.ts, card-preview.ts, card-pdf.ts
 */

export interface ZoneLayout {
  imagePos: [string, string];
  imageSize: [string, string];
  bluePillPos: [string, string];
  bluePillSize: [string, string];
  orangePillPos: [string, string];
  orangePillSize: [string, string];
  textboxPos: [string, string];
  textboxSize: [string, string];
  textboxPosNoOrange: [string, string];
  textboxSizeNoOrange: [string, string];
}

export const PAGE_WIDTH_IN = 13.33333;
export const PAGE_HEIGHT_IN = 7.5;
export const DPI = 96;
export const PAGE_WIDTH_PX = Math.round(PAGE_WIDTH_IN * DPI);   // 1280
export const PAGE_HEIGHT_PX = Math.round(PAGE_HEIGHT_IN * DPI); // 720

export const BLUE_FILL = "#2c98ff";
export const ORANGE_FILL = "#ff7100";
export const TITLE_COLOR = "#3f3f3f";
export const BODY_COLOR = "#0c0c0c";
export const HIGHLIGHT_COLOR = "#000000";
export const PILL_TEXT_COLOR = "#ffffff";

export const FONT_CJK = '"Microsoft JhengHei", "PingFang TC", "Noto Sans CJK TC", sans-serif';
export const FONT_LATIN = "Calibri, Arial, sans-serif";
export const PILL_FONT_SIZE_IN = 0.19444;
export const TITLE_FONT_SIZE_IN = 0.33333;

export const TITLE_POS = { x: 0.7017, y: 0.43015, w: 11.92979, h: 0.625 };
export const PAGENUM_POS = { x: 12.60052, y: 6.98548 };

export const LEFT_ZONE: ZoneLayout = {
  imagePos: ["1.84in", "1.04in"],
  imageSize: ["3.27in", "3.28in"],
  bluePillPos: ["1.58in", "4.44in"],
  bluePillSize: ["3.83in", "0.58in"],
  orangePillPos: ["1.58in", "5.14in"],
  orangePillSize: ["3.83in", "0.40in"],
  textboxPos: ["0.43in", "5.66in"],
  textboxSize: ["6.16in", "1.41in"],
  textboxPosNoOrange: ["0.43in", "5.16in"],
  textboxSizeNoOrange: ["6.16in", "1.91in"],
};

export const RIGHT_ZONE: ZoneLayout = {
  imagePos: ["8.62in", "0.94in"],
  imageSize: ["2.63in", "3.27in"],
  bluePillPos: ["8.02in", "4.30in"],
  bluePillSize: ["3.83in", "0.38in"],
  orangePillPos: ["7.90in", "4.79in"],
  orangePillSize: ["4.06in", "0.40in"],
  textboxPos: ["7.27in", "5.33in"],
  textboxSize: ["5.66in", "1.74in"],
  textboxPosNoOrange: ["7.27in", "4.83in"],
  textboxSizeNoOrange: ["5.66in", "2.24in"],
};

/** Parse "1.84in" → 1.84 */
export function parseIn(s: string): number {
  return parseFloat(s.replace("in", ""));
}

/** Inches → pixels at 96 DPI */
export function inToPx(inches: number): number {
  return Math.round(inches * DPI);
}

const PILL_SHIFT_UP = 0.10;

export interface PillRect { x: number; y: number; w: number; h: number }

export function pillLayout(zone: ZoneLayout, hasOrange: boolean): {
  blue: PillRect;
  orange: PillRect | null;
} {
  const bpX = parseIn(zone.bluePillPos[0]);
  const bpY = parseIn(zone.bluePillPos[1]);
  const bpW = parseIn(zone.bluePillSize[0]);
  const bpH = parseIn(zone.bluePillSize[1]);

  if (!hasOrange) {
    return { blue: { x: bpX, y: bpY, w: bpW, h: bpH }, orange: null };
  }

  const opX = parseIn(zone.orangePillPos[0]);
  const opY = parseIn(zone.orangePillPos[1]);
  const opW = parseIn(zone.orangePillSize[0]);
  const opH = parseIn(zone.orangePillSize[1]);

  const gap = opY - (bpY + bpH);
  const adjBpY = bpY - PILL_SHIFT_UP;
  const adjBpH = opH;
  const adjOpY = adjBpY + adjBpH + gap;

  return {
    blue: { x: bpX, y: adjBpY, w: bpW, h: adjBpH },
    orange: { x: opX, y: adjOpY, w: opW, h: opH },
  };
}

export function pillLayoutStr(zone: ZoneLayout, hasOrange: boolean): {
  bluePos: [string, string]; blueSize: [string, string];
  orangePos: [string, string]; orangeSize: [string, string];
} {
  const p = pillLayout(zone, hasOrange);
  const fmt = (v: number) => `${v.toFixed(5)}in`;
  return {
    bluePos: [fmt(p.blue.x), fmt(p.blue.y)],
    blueSize: [fmt(p.blue.w), fmt(p.blue.h)],
    orangePos: p.orange ? [fmt(p.orange.x), fmt(p.orange.y)] : zone.orangePillPos,
    orangeSize: p.orange ? [fmt(p.orange.w), fmt(p.orange.h)] : zone.orangePillSize,
  };
}
